// Marque affichée selon la langue du visiteur.
//   français : lien.quebec (plateforme québécoise)
//   anglais  : mylink.quebec (même site, en anglais, prix en CAD)
//   mylink.me : marque internationale (USD, sans mention du Québec)

export const BRAND_FR = 'lien.quebec';
// Version anglaise de lien.quebec : mêmes prix (CAD), mentions du Québec gardées.
export const BRAND_EN_QC = 'mylink.quebec';
// Marque internationale : en anglais, en USD, sans mention du Québec.
export const BRAND_INTL = 'mylink.me';
export const BRAND_EN = BRAND_INTL;

// Adresse qui reçoit les demandes de services clé en main (installation,
// design de page). À changer ici seulement.
export const SERVICES_EMAIL = 'info@lien.quebec';

function currentHost(): string {
  if (typeof window === 'undefined') return '';
  return window.location.hostname.toLowerCase();
}

function hostIs(domain: string): boolean {
  const host = currentHost();
  return host === domain || host.endsWith(`.${domain}`);
}

// Vrai seulement sur mylink.me (marque internationale).
export function onEnglishDomain(): boolean {
  return hostIs(BRAND_INTL);
}

// Vrai sur mylink.quebec (lien.quebec en anglais).
export function onQuebecEnglishDomain(): boolean {
  return hostIs(BRAND_EN_QC);
}

// Nom de la marque : mylink.me sur mylink.me, sinon mylink.quebec en
// anglais et lien.quebec en français.
export function brandName(en: boolean): string {
  if (onEnglishDomain()) return BRAND_INTL;
  return en ? BRAND_EN_QC : BRAND_FR;
}

// « lien.quebec/ », « mylink.quebec/ » ou « mylink.me/ », devant le nom
// choisi par le client : toujours le domaine réellement visité.
export function addressPrefix(_en?: boolean): string {
  if (onEnglishDomain()) return `${BRAND_INTL}/`;
  if (onQuebecEnglishDomain()) return `${BRAND_EN_QC}/`;
  return `${BRAND_FR}/`;
}

// Adapte un texte pour la version anglaise : remplace la marque française
// par la marque anglaise du domaine visité.
export function brandText(text: string, en: boolean): string {
  if (!en || !text) return text;
  return text
    .replace(/lien\.quebec\//gi, '\u0000ADDR\u0000')
    .replace(/lien\.quebec/gi, brandName(true))
    .replace(/\u0000ADDR\u0000/g, addressPrefix(true));
}

// Les traductions anglaises sont écrites avec « mylink.me ». Ailleurs que
// sur mylink.me, la marque anglaise est mylink.quebec : on adapte donc les
// textes traduits (et leurs clés) une seule fois par jeu de traductions.
const adaptedLines = new WeakMap<object, Record<string, string>>();
export function brandLines<T extends Record<string, string> | undefined>(
  lines: T,
): T {
  if (!lines || onEnglishDomain()) return lines;
  const cached = adaptedLines.get(lines);
  if (cached) return cached as T;
  const out: Record<string, string> = {};
  for (const key of Object.keys(lines)) {
    const value = lines[key];
    const next =
      typeof value === 'string' && value.includes(BRAND_INTL)
        ? value.split(BRAND_INTL).join(BRAND_EN_QC)
        : value;
    out[key] = next;
    if (key.includes(BRAND_INTL)) {
      out[key.split(BRAND_INTL).join(BRAND_EN_QC)] = next;
    }
  }
  adaptedLines.set(lines, out);
  return out as T;
}

// Sur mylink.quebec, les mentions du Québec restent : voici leur version
// anglaise (textes par défaut de la page d'accueil).
const QUEBEC_LINES_EN: Record<string, string> = {
  'La plateforme québécoise pour vos liens': 'The Quebec platform for your links',
  'Est-ce une plateforme québécoise?': 'Is it a Quebec platform?',
  'Oui. La plateforme est conçue et hébergée au Québec, par une équipe d’ici, avec un service en français et en anglais.':
    'Yes. The platform is designed and hosted in Quebec, by a local team, with service in French and English.',
};

const MENTIONS_QUEBEC = /qu[ée]b[ée]c/i;

// Version anglaise des sections de la page d'accueil. Sur mylink.me :
// marque mylink.me, aucune mention du Québec. Ailleurs : marque
// mylink.quebec, mentions du Québec gardées et traduites.
export function localizeLandingSections(sections: any[], en: boolean): any[] {
  if (!en) return sections;
  // Les mentions du Québec ne sont retirées que sur mylink.me.
  const international = onEnglishDomain();

  const walk = (value: any): any => {
    if (typeof value === 'string') {
      if (!international && QUEBEC_LINES_EN[value.trim()]) {
        return QUEBEC_LINES_EN[value.trim()];
      }
      return brandText(value, true);
    }
    if (Array.isArray(value)) return value.map(walk);
    if (value && typeof value === 'object') {
      const copy: Record<string, any> = {};
      for (const key of Object.keys(value)) {
        // les adresses d'images et de liens ne sont pas des textes à adapter
        copy[key] =
          key === 'src' || key === 'action' || key === 'name' || key === 'icon'
            ? value[key]
            : walk(value[key]);
      }
      return copy;
    }
    return value;
  };

  return sections.map(section => {
    if (!section || typeof section !== 'object') return section;
    let next = section;
    // la FAQ : on retire les questions qui portent sur le Québec
    if (international && section.name === 'faq' && Array.isArray(section.questions)) {
      next = {
        ...section,
        questions: section.questions.filter(
          (q: any) =>
            !MENTIONS_QUEBEC.test(
              `${q?.question ?? ''}`.replace(/lien\.quebec/gi, ''),
            ),
        ),
      };
    }
    // le badge du haut de page
    if (
      international &&
      typeof next.badge === 'string' &&
      MENTIONS_QUEBEC.test(next.badge.replace(/lien\.quebec/gi, ''))
    ) {
      next = {...next, badge: 'The link platform that updates itself'};
    }
    next = walk(next);
    // toute autre phrase qui mentionne encore le Québec est retirée des réponses
    if (international && next.name === 'faq' && Array.isArray(next.questions)) {
      next = {
        ...next,
        questions: next.questions.map((q: any) => ({
          ...q,
          answer:
            typeof q?.answer === 'string'
              ? q.answer
                  .split(/(?<=[.!?])\s+/)
                  .filter(
                    (sentence: string) =>
                      !MENTIONS_QUEBEC.test(
                        sentence.replace(/lien\.quebec/gi, ''),
                      ),
                  )
                  .join(' ')
              : q?.answer,
        })),
      };
    }
    return next;
  });
}
